import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import cardService from '../services/cardService';
import inventoryService from '../services/inventoryService';
import slugService from '../services/slugService';
import productService from '../services/productService';
import insertSetService from '../services/insertSetService';
import userService from '../services/userService';
import CardDetailsPage from '../components/CardDetailsPage';
import usePageTitle from '../utils/usePageTitle';
import Sidebar from '../components/Sidebar';
import SidebarSection from '../components/Sidebar/SidebarSection';
import SidebarRow from '../components/Sidebar/SidebarRow';
import SidebarNote from '../components/Sidebar/SidebarNote';
import SidebarImage from '../components/Sidebar/SidebarImage';
import UserContactCard from '../components/UserContactCard';
import UserCollectionLink from '../components/UserCollectionLink';
import CardQuantityAdjuster from '../components/CardQuantityAdjuster';
import WishlistIcon from '../components/WishlistIcon';
import TradeIcon from '../components/TradeIcon';
import { getUserCollectionCardBreadcrumbs } from '../utils/breadcrumbUtils';

/**
 * Displays a card from a specific user's collection
 */
const UserCollectionCardPage = ({ username, cardSlug }) => {
  const { currentUser } = useContext(AuthContext);
  
  const [userData, setUserData] = useState(null);
  const [card, setCard] = useState(null);
  const [product, setProduct] = useState(null);
  const [insertSet, setInsertSet] = useState(null);
  
  // Set page title with user's collection first, then card hierarchy
  usePageTitle(
    userData && card && insertSet && product
      ? [
          `${userData.name}'s Collection`,
          card.name,
          insertSet.name,
          `${product.name} (${product.year})`
        ]
      : 'Card Details'
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inventoryItem, setInventoryItem] = useState(null);
  const [isOwner, setIsOwner] = useState(false);

  // Fetch user data and card details
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!username || !cardSlug) {
          throw new Error('Username and card slug are required');
        }
        
        // Fetch user data first
        const user = await userService.getUserByUsername(username);
        setUserData(user);
        // Check if the current user is the owner of this collection
        setIsOwner(currentUser && currentUser.id === user.id);
        // Extract card ID from slug
        const parsedSlug = slugService.parseSlugWithId(cardSlug);
        if (!parsedSlug.id) {
          throw new Error('Could not extract ID from card slug');
        }
        // Fetch the inventory item to get user-specific data (quantity, forTrade, etc.)
        const inventory = await inventoryService.getUserInventoryByCardAndUser(parsedSlug.id, user.id);
        setInventoryItem(inventory);
        // Fetch the card data
        const cardData = await cardService.getCardById(parsedSlug.id);
        setCard(cardData);
        // Fetch related product and insert set
        if (cardData.productId) {
          const productData = await productService.getProductById(cardData.productId);
          setProduct(productData);
        }
        if (cardData.insertSetId) {
          const insertSetData = await insertSetService.getInsertSetById(cardData.insertSetId);
          setInsertSet(insertSetData);
        }
      } catch (err) {
        setError('Failed to load collection card details');
        console.error('Error fetching collection card details:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [username, cardSlug, currentUser]);

  // Render the sidebar with user, product, and card information
  const renderSidebar = () => {
    if (!userData || !card) return null;
    
    return (
      <Sidebar title={<UserCollectionLink user={userData} />}>
        {/* Product image if available */}
        {product && product.imageUrl && (
          <SidebarImage 
            src={product.imageUrl}
            alt={product.name}
          />
        )}
        {/* Product info if available */}
        {product && (
          <SidebarSection title="Product Details">
            <SidebarRow
              label="Product"
              value={
                <Link to={slugService.getUserCollectionProductUrl(product, userData.username)}>
                  {product.name}
                </Link>
              }
            />
            <SidebarRow
              label="Year"
              value={product.year}
            />
            {product.manufacturer && (
              <SidebarRow
                label="Manufacturer"
                value={product.manufacturer}
              />
            )}
          </SidebarSection>
        )}
        
        {/* Insert set info if available */}
        {insertSet && product && (
          <SidebarSection title="Insert Set Details">
            <SidebarRow
              label="Set Name"
              value={
                <Link to={slugService.getUserCollectionInsertSetUrl(insertSet, product, userData.username)}>
                  {insertSet.name}
                </Link>
              }
            />
            {insertSet.description && (
              <SidebarRow
                label="Description"
                value={insertSet.description}
              />
            )}
            {insertSet.totalCards && (
              <SidebarRow
                label="Total Cards"
                value={insertSet.totalCards}
              />
            )}
          </SidebarSection>
        )}
        
        {/* Card details */}
        <SidebarSection title="Card Details">
          {card.cardNumber && (
            <SidebarRow
              label="Card Number"
              value={card.cardNumber}
            />
          )}
          {inventoryItem && (
            <SidebarRow
              label="Quantity"
              value={inventoryItem.quantity}
            />
          )}
          {inventoryItem && inventoryItem.forTrade && (
            <SidebarRow
              label="For Trade"
              value="Yes"
            />
          )}
          {inventoryItem && inventoryItem.acquiredDate && (
            <SidebarRow
              label="Acquired"
              value={new Date(inventoryItem.acquiredDate).toLocaleDateString()}
            />
          )}
        </SidebarSection>
        
        {/* Private note for owner */}
        {isOwner && inventoryItem && inventoryItem.notes && (
          <SidebarSection title="Your Private Notes">
            <SidebarNote>
              {inventoryItem.notes}
            </SidebarNote>
          </SidebarSection>
        )}

        {/* User info card */}
        <UserContactCard user={userData} />

      </Sidebar>
    );
  };

  // Prepare navigation links
  const navigationLinks = {
    back: product ? {
      url: slugService.getUserCollectionProductUrl(product, username),
      label: `Back to ${username}'s ${product?.name}`
    } : {
      url: `/@${username}/collection`,
      label: `Back to ${username}'s Collection`
    },
    view: insertSet && product ? {
      url: slugService.getUserCollectionInsertSetUrl(insertSet, product, username),
      label: `View all ${insertSet.name} cards`
    } : null
  };

  // Left side actions (quantity adjuster for owner)
  const leftActions = isOwner && card && (
    <CardQuantityAdjuster 
      card={{ ...card, quantity: inventoryItem ? inventoryItem.quantity : 0 }} 
      className="card-details__icon"
      size={40}
    />
  );

  // Right side actions (trade and wishlist icons for owner)
  const rightActions = isOwner && card && (
    <>
      <TradeIcon card={card} className="card-details__icon" size={40} />
      <WishlistIcon card={card} className="card-details__icon" size={40} />
    </>
  );

  // Generate breadcrumb items for user collection card page
  const breadcrumbItems = userData && card && product && insertSet 
    ? getUserCollectionCardBreadcrumbs(userData, product, insertSet, card)
    : null;

  return (
    <CardDetailsPage
      card={card}
      loading={loading}
      error={error}
      cardNumber={card?.cardNumber}
      title={card ? `${username}'s ${card.name}` : ""}
      imageUrl={card?.imageUrl}
      description={card?.description}
      navigationLinks={navigationLinks}
      leftActions={leftActions}
      rightActions={rightActions}
      renderSidebar={renderSidebar}
      breadcrumbItems={breadcrumbItems}
    />
  );
};

export default UserCollectionCardPage;